import { take } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { SitesService, Site } from '@app/data/services/sitesService';
import { FilterService } from '@app/data/services/filterService';
import { SelectedLocationService, SiteLocation } from '@app/data/services/locationServices';
import { ChartService } from '@app/data/services/chartService';
import { formatDateForApi } from '@app/data/helpers/dataHelpers';
import { EChartsOption } from 'echarts';
import { getWaiQualityChartConfig, WaiQualityDataByDate, WaiQualityData } from './rongo-wai-quality-chart-helpers';
import { RongoReportingData, RongoSourceGroup } from '@app/widgets/helpers/types';
import { withOrganisationBreakdown, renderMetricTable } from '@app/widgets/helpers/organisation-breakdown';

/**
 * Only intended use is for Rongo Wai Quality
 */
@Component({
  selector: 'app-rongo-wai-quality-chart',
  templateUrl: './rongo-wai-quality-chart.component.html',
  styleUrls: ['./rongo-wai-quality-chart.component.scss'],
})
export class RongoWaiQualityChartComponent implements OnInit {
  chartTitle: string = 'Rongo Wai';
  chartSubtitle: string = '';
  chartOptions?: EChartsOption;
  id: string = 'rongo-wai-quality';
  startDate: any = '';
  endDate: any = '';
  location: SiteLocation | null = null;
  noData: boolean = true; // We can do this check in html ngIf, but it's hard to read for some widgets
  site: Site | null = null;
  breakdown: RongoSourceGroup<WaiQualityDataByDate>[] | null = null;

  constructor(
    private filterService: FilterService,
    private sitesService: SitesService,
    private chartService: ChartService,
    private selectedLocationService: SelectedLocationService
  ) {}

  ngOnInit() {
    this.selectedLocationService.subscribe(this.handleLocationUpdate);
    this.startDate = formatDateForApi(this.filterService.getFilters().fromDate);
    this.endDate = formatDateForApi(this.filterService.getFilters().toDate);
    this.filterService.subscribe(this.handleFilterUpdate);
    this.sitesService
      .getSelectedSite()
      .pipe(take(1))
      .subscribe((site) => {
        this.handleSiteUpdate({ site });
        this.sitesService.subscribe(this.handleSiteUpdate);
      });
  }
  setData = () => {
    if (!this.startDate || !this.endDate || !this.site) return;
    this.chartService
      .getWaiQualityData(this.startDate, this.endDate, this.location ? this.location.id : null, 'organisation')
      .subscribe(
        (data: RongoReportingData<WaiQualityDataByDate>) => {
          const { config, data: waiQualityData, breakdown } = data;
          this.chartTitle = config?.title || '';
          this.chartSubtitle = config?.subTitle || '';
          this.breakdown = breakdown?.groups || null;
          this.noData =
            waiQualityData === undefined ||
            Object.values(waiQualityData).every((entry: WaiQualityData) => {
              return (
                entry.dominatWaterColour === 'N/A' &&
                entry.waterClarity.value === 0 &&
                entry.riparianCoverageText.value === 0 &&
                entry.taongaWaiSpeciesAbundance.value === 0
              );
            });
          this.chartOptions = withOrganisationBreakdown(getWaiQualityChartConfig(waiQualityData), this.breakdown, {
            buildGroupOption: (d) => getWaiQualityChartConfig(d),
            renderFlat: (groups) =>
              renderMetricTable(groups, [
                { key: 'waterClarity', label: 'Water Clarity' },
                { key: 'riparianCoverageText', label: 'Riparian Coverage' },
                { key: 'taongaWaiSpeciesAbundance', label: 'Taonga Wai Abundance' },
                { key: 'dominatWaterColour', label: 'Dominant Water Colour' },
              ]),
          });
        },
        (error: any) => {
          console.error('Error fetching data:', error);
        }
      );
  };
  handleSiteUpdate = ({ site }: any) => {
    if (site && site.id != this.site?.id) {
      this.site = site;
      this.setData();
    }
  };
  handleLocationUpdate = (location: SiteLocation | null) => {
    if (!location && !this.location) return;
    if (location?.id != this.location?.id) {
      this.location = location;
      this.setData();
    }
  };
  handleFilterUpdate = (filters: any) => {
    if (!filters.filters.fromDate || !filters.filters.toDate) return; // Bail out until we have full date range

    const newStartDate = formatDateForApi(filters.filters.fromDate);
    const newEndDate = formatDateForApi(filters.filters.toDate);
    const sharedFiltersChanged = this.sharedRongoReportFiltersChanged(filters.changedFilters);
    if (
      (newStartDate !== this.startDate || newEndDate !== this.endDate || sharedFiltersChanged) &&
      !!newStartDate &&
      !!newEndDate
    ) {
      this.startDate = newStartDate;
      this.endDate = newEndDate;
      this.setData();
    }
  };

  private sharedRongoReportFiltersChanged(changedFilters: string[] | undefined): boolean {
    return !!changedFilters?.some((filter) =>
      ['selectedContributingOrganisations', 'selectedContributingProjectListItems'].includes(filter)
    );
  }
}
